import React, { useState, useCallback } from "react";
import { Download, Loader2, FileText, Image, FileSpreadsheet, AlertCircle } from "lucide-react";
import { caiApi } from "../api";

const MD_DOWNLOAD_PREFIX = "md_download/";

/** Check if a URL is an md_download path */
export function isMdDownload(url: string | undefined | null): boolean {
  return !!url && url.includes(MD_DOWNLOAD_PREFIX);
}

/** Extract a human-readable filename from an md_download path */
function extractFilename(path: string): string {
  return path.split("/").pop() ?? "file";
}

/** Get an icon based on file extension */
function getFileIcon(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(ext)) return Image;
  if (["csv", "xlsx", "xls"].includes(ext)) return FileSpreadsheet;
  return FileText;
}

interface MdDownloadLinkProps {
  href: string;
  children?: React.ReactNode;
}

/**
 * Renders md_download links as styled download buttons.
 * Fetches the file via authenticated API call and opens/downloads it.
 */
export function MdDownloadLink({ href, children }: MdDownloadLinkProps) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const filename = extractFilename(href);
  const FileIcon = getFileIcon(filename);

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    setState("loading");
    try {
      const blobUrl = await caiApi.fetchMdDownload(href);
      // For images, open in new tab; for other files, trigger download
      const ext = filename.split(".").pop()?.toLowerCase() ?? "";
      if (["png", "jpg", "jpeg", "gif", "svg", "webp"].includes(ext)) {
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      } else {
        // Trigger download
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setState("idle");
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 3000);
    }
  }, [href, filename]);

  return (
    <button
      onClick={handleClick}
      disabled={state === "loading"}
      className="inline-flex items-center gap-1.5 bg-accent-violet/10 hover:bg-accent-violet/20 text-accent-violet rounded-lg px-2.5 py-1.5 text-[12px] font-medium transition-colors cursor-pointer border border-accent-violet/20 my-1 max-w-full"
    >
      {state === "loading" ? (
        <Loader2 size={13} className="shrink-0 animate-spin" />
      ) : state === "error" ? (
        <AlertCircle size={13} className="shrink-0 text-ca-punch" />
      ) : (
        <FileIcon size={13} className="shrink-0" />
      )}
      <span className="truncate">{children ?? filename}</span>
      {state === "idle" && <Download size={11} className="shrink-0 opacity-60" />}
      {state === "error" && <span className="text-[10px] opacity-70">Failed</span>}
    </button>
  );
}
